import * as React from 'react';

import { cn } from '@/lib/utils';

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  wrapperClassName?: string;
  wrapperRef?: any;
  onWrapperScroll?: React.UIEventHandler<HTMLDivElement>;
  showTopScrollbar?: boolean;
};

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      wrapperClassName,
      wrapperRef,
      onWrapperScroll,
      showTopScrollbar = false,
      children,
      ...props
    },
    ref
  ) => {
    const containerRef = React.useRef<any>(null);
    const tableRef = React.useRef<HTMLTableElement>(null);
    const topScrollbarRef = React.useRef<HTMLDivElement>(null);
    const syncingTopScrollbarRef = React.useRef(false);
    const [isScrollable, setIsScrollable] = React.useState(false);
    const [topScrollbarWidth, setTopScrollbarWidth] = React.useState(0);

    const setRefs = React.useCallback(
      (node: HTMLTableElement | null) => {
        // @ts-ignore
        tableRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    const setWrapperRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof wrapperRef === 'function') {
          wrapperRef(node);
        } else if (wrapperRef) {
          wrapperRef.current = node;
        }
      },
      [wrapperRef]
    );

    const updateScrollable = React.useCallback(() => {
      const container = containerRef.current;
      if (!container) return;
      const table = tableRef.current;
      const measuredTableWidth = Math.ceil(
        Math.max(
          table?.scrollWidth || 0,
          table?.offsetWidth || 0,
          table?.getBoundingClientRect().width || 0
        )
      );
      const scrollWidth = Math.max(measuredTableWidth || container.scrollWidth, container.clientWidth);
      setIsScrollable(scrollWidth > container.clientWidth + 1);
      setTopScrollbarWidth(scrollWidth);
    }, []);

    const syncTopScrollbar = React.useCallback((scrollLeft: number) => {
      const topScrollbar = topScrollbarRef.current;
      if (!topScrollbar || topScrollbar.scrollLeft === scrollLeft) return;
      syncingTopScrollbarRef.current = true;
      topScrollbar.scrollLeft = scrollLeft;
      requestAnimationFrame(() => {
        syncingTopScrollbarRef.current = false;
      });
    }, []);

    const handleWrapperScroll: React.UIEventHandler<HTMLDivElement> = React.useCallback(
      (event) => {
        if (showTopScrollbar && !syncingTopScrollbarRef.current) {
          syncTopScrollbar(event.currentTarget.scrollLeft);
        }
        onWrapperScroll?.(event);
      },
      [onWrapperScroll, showTopScrollbar, syncTopScrollbar]
    );

    const handleTopScrollbarScroll: React.UIEventHandler<HTMLDivElement> = React.useCallback(
      (event) => {
        const container = containerRef.current;
        if (!container || container.scrollLeft === event.currentTarget.scrollLeft) return;
        syncingTopScrollbarRef.current = true;
        container.scrollLeft = event.currentTarget.scrollLeft;
        requestAnimationFrame(() => {
          syncingTopScrollbarRef.current = false;
        });
      },
      []
    );

    React.useLayoutEffect(() => {
      updateScrollable();
      const frame = requestAnimationFrame(updateScrollable);
      if (typeof ResizeObserver === 'undefined') {
        return () => cancelAnimationFrame(frame);
      }
      const observer = new ResizeObserver(updateScrollable);
      const container = containerRef.current;
      if (container) {
        observer.observe(container);
      }
      if (tableRef.current) {
        observer.observe(tableRef.current);
      }
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
      };
    }, [updateScrollable]);

    React.useLayoutEffect(() => {
      if (!isScrollable) return;
      syncTopScrollbar(containerRef.current?.scrollLeft || 0);
    }, [isScrollable, syncTopScrollbar, topScrollbarWidth]);

    return (
      <div
        ref={setWrapperRefs}
        onScroll={handleWrapperScroll}
        data-scrollable={isScrollable ? 'true' : 'false'}
        data-top-scrollbar={showTopScrollbar && isScrollable ? 'true' : 'false'}
        className={cn(
          'group relative overflow-x-auto border border-input rounded-md max-w-[calc(100vw-77px-1rem)] max-h-[calc(100vh-270px)] md:max-w-[calc(100vw-113px-1rem)] md:max-h-[calc(100vh-270px)]',
          showTopScrollbar && 'quipa-hide-bottom-horizontal-scrollbar',
          wrapperClassName
        )}
      >
        {showTopScrollbar && isScrollable ? (
          <div
            ref={topScrollbarRef}
            onScroll={handleTopScrollbarScroll}
            aria-hidden="true"
            className="sticky left-0 top-0 z-[60] h-[14px] w-full overflow-x-auto overflow-y-hidden bg-white"
          >
            <div style={{ width: topScrollbarWidth, height: 1 }} />
          </div>
        ) : null}
        <table ref={setRefs} className={cn('w-full caption-bottom text-sm', className)} {...props}>
          {children}
        </table>
      </div>
    );
  }
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-0', className)} {...props}>
    {children}
  </thead>
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props}>
    {children}
  </tbody>
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('bg-muted/50 font-medium text-foreground [&>tr]:last:border-b-0', className)}
    {...props}
  >
    {children}
  </tfoot>
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, children, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'border-b border-input transition-colors bg-white hover:bg-[#F8FAFC]',
        className
      )}
      {...props}
    >
      {children}
    </tr>
  )
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, children, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'sticky top-0 z-50 bg-table-header h-12 text-left align-middle text-[12px] font-semibold text-muted-foreground shadow-[inset_0_-1px_0_0_rgb(226,232,240)] p-0',
      'group-data-[top-scrollbar=true]:top-[14px]',
      '[&:has([role=checkbox])]:pr-0',
      'last:right-0 last:w-[55px] last:bg-table-header group-data-[scrollable=true]:last:shadow-[inset_1px_-1px_0_0_rgb(226,232,240)]',
      className
    )}
    {...props}
  >
    {children}
  </th>
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, children, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'max-w-max whitespace-nowrap px-[12px] py-[16px] align-middle text-[13px] text-foreground [&:has([role=checkbox])]:pr-0',
      'last:align-middle last:text-center last:w-[55px] last:sticky last:right-[0px] last:bg-inherit group-data-[scrollable=true]:last:shadow-[inset_1px_0_0_0_rgb(226,232,240)]',
      className
    )}
    {...props}
  >
    {children}
  </td>
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, children, ...props }, ref) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props}>
    {children}
  </caption>
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
